export type QuotaMetric =
  | 'scans'
  | 'api_calls'
  | 'concurrent_agents'
  | 'content_suggestions'
  | 'brand_narrative_runs'
  | 'brand_authority_runs'
  | 'brand_referral_runs';

export interface QuotaRecord {
  tenantId: string;
  period: string;
  metric: QuotaMetric;
  used: number;
  limit: number;
  ttl: number;
}

export interface TierLimits {
  /** @deprecated Use brands instead */
  sites: number;
  brands: number;
  zones: number;
  scansPerMonth: number;
  apiCallsPerDay: number;
  concurrentAgents: number;
  auditTypes: string[];
  crawlMode: string;
  /**
   * Max pages fetched + analyzed per Website Audit run. Applied at crawl time
   * (CrawlAgent truncates the triaged URL list to this cap) and at analysis
   * time (each Phase-1 agent re-reads the manifest and processes up to this
   * many pages). Agents fall back to a hardcoded 10/30/100 dict when the
   * SNS payload lacks the field, so older jobs keep working.
   */
  pagesPerAudit: number;
  socialIntegrations: number;
  users: number;
  competitors: number;
  contentSuggestions: boolean;
  webhookAccess: boolean;
  newsWatchMaxProposals: number;

  // --- Brand Perception (split into 3 axes) ---
  // Caps on the NUMBER of active query rows the user can maintain per axis.
  // Referral is specified per-level — effective count is perLevel × 5 × Nzones.
  brandNarrativeQueries: number;
  brandAuthorityQueries: number;
  brandReferralQueriesPerLevel: number;
  // Monthly audit-run caps per axis (audits / month).
  brandNarrativeRunsPerMonth: number;
  brandAuthorityRunsPerMonth: number;
  brandReferralRunsPerMonth: number;
}

/**
 * Resolve effective limits for a tenant: tier defaults with per-tenant overrides applied on top.
 * A tenant's `settings.quotaOverrides` takes precedence over the tier defaults, so an
 * `enterprise` tenant can have negotiated caps set individually, and a `lite`/`pro` tenant
 * can have a single field bumped without switching tiers.
 */
export function resolveLimits(
  tier: string,
  overrides?: Partial<TierLimits>,
): TierLimits {
  const base = TIER_LIMITS[tier];
  if (!base) {
    throw new Error(`Unknown tier: ${tier}`);
  }
  if (!overrides) return base;
  return { ...base, ...overrides };
}

/** -1 means unlimited */
export const TIER_LIMITS: Record<string, TierLimits> = {
  lite: {
    sites: 1,
    brands: 1,
    zones: 1,
    scansPerMonth: 4,
    apiCallsPerDay: 100,
    concurrentAgents: 1,
    // Brand Perception axes are available on every tier (even lite) — query count
    // is the tier differentiator, not feature gating.
    auditTypes: ['website', 'brand_narrative', 'brand_authority', 'brand_referral'],
    crawlMode: 'sitemap',
    pagesPerAudit: 10,
    socialIntegrations: 0,
    users: 2,
    competitors: 0,
    contentSuggestions: false,
    webhookAccess: false,
    newsWatchMaxProposals: 1,
    brandNarrativeQueries: 1,
    brandAuthorityQueries: 1,
    brandReferralQueriesPerLevel: 1,
    brandNarrativeRunsPerMonth: 2,
    brandAuthorityRunsPerMonth: 2,
    brandReferralRunsPerMonth: 2,
  },
  pro: {
    sites: 3,
    brands: 3,
    zones: 5,
    scansPerMonth: 30,
    apiCallsPerDay: 1000,
    concurrentAgents: 3,
    auditTypes: [
      'website',
      'brand_narrative',
      'brand_authority',
      'brand_referral',
      'social',
      'news_watch',
    ],
    crawlMode: 'full',
    pagesPerAudit: 30,
    socialIntegrations: 5,
    users: 10,
    competitors: 3,
    contentSuggestions: true,
    webhookAccess: false,
    newsWatchMaxProposals: 3,
    brandNarrativeQueries: 2,
    brandAuthorityQueries: 2,
    brandReferralQueriesPerLevel: 2,
    brandNarrativeRunsPerMonth: 8,
    brandAuthorityRunsPerMonth: 8,
    brandReferralRunsPerMonth: 8,
  },
  enterprise: {
    sites: -1,
    brands: -1,
    zones: -1,
    scansPerMonth: -1,
    apiCallsPerDay: -1,
    concurrentAgents: 10,
    auditTypes: [
      'website',
      'brand_narrative',
      'brand_authority',
      'brand_referral',
      'social',
      'news_watch',
    ],
    crawlMode: 'full',
    pagesPerAudit: 100,
    socialIntegrations: -1,
    users: -1,
    competitors: -1,
    contentSuggestions: true,
    webhookAccess: true,
    newsWatchMaxProposals: 3,
    brandNarrativeQueries: 3,
    brandAuthorityQueries: 3,
    brandReferralQueriesPerLevel: 3,
    brandNarrativeRunsPerMonth: 30,
    brandAuthorityRunsPerMonth: 30,
    brandReferralRunsPerMonth: 30,
  },
};
